/**
 * PF-3b (fieldiq-api#1224) — a dig report's Summary, on the report card (dig
 * page and report page).
 *
 *   POST  /reports/:id/ai-summary           the AI drafts it into a pending run; the report is not changed
 *   GET   /reports/:id → aiDraft            that pending draft (always null on an issued report)
 *   POST  /reports/:id/ai-summary/approve   { run_id, summary? } — summary only when the person edited it
 *   POST  /reports/:id/ai-summary/dismiss   { run_id }
 *   PATCH /reports/:id { ai_summary }       a person's own summary
 *
 * THE AI'S TEXT IS A DRAFT UNTIL A PERSON APPROVES IT. It is shown under "AI
 * draft — awaiting your approval", in an editable box, and reaches the report
 * (and the PDF's Summary) only through Approve. The approved text is the
 * report's Summary. An issued report shows the Summary read-only and offers no
 * AI action. Every refusal — 409 report_issued, 502 ai_draft_failed — is a
 * sentence in the refusal style, never a toast and never a success colour.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useApprovePfReportSummary, useDismissPfReportSummary, useDraftPfReportSummary, useUpdatePfReport } from '../../../hooks/usePfDigs';
import { formatInstant } from '../../../lib/calendar-date';
import { pfDigRefusal } from '../../../lib/pf-digs';
import type { PfReport, PfReportAiDraft } from '../../../types/pf-digs';
import { PF_PRIMARY, PF_SECONDARY, PfFormError } from '../PfBits';

const LABEL = 'text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';

/**
 * A summary box that shows the whole text. PF_INPUT carries a one-line height, which
 * beat the taller class added beside it and left the AI draft two lines tall: a reviewer could not
 * read what they were approving. So: no fixed height, at least 8 rows, and the
 * box grows to its content on every change and every resize.
 */
const SUMMARY_TEXTAREA =
  'block w-full resize-y rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm leading-relaxed text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none disabled:opacity-60';

function SummaryTextarea(props: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows' | 'className'>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const fit = () => {
      const el = ref.current;
      if (!el) return;
      el.style.height = 'auto';
      if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight + 2}px`;
    };
    fit();
    // A narrower screen wraps the same text onto more lines: measure again.
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [props.value]);
  return <textarea ref={ref} rows={8} className={SUMMARY_TEXTAREA} {...props} />;
}

export function PfReportSummary({
  report,
  canWrite,
  timeZone,
}: {
  report: PfReport & { aiDraft?: PfReportAiDraft | null };
  canWrite: boolean;
  timeZone: string | null | undefined;
}) {
  const draftAi = useDraftPfReportSummary();
  const approve = useApprovePfReportSummary();
  const dismiss = useDismissPfReportSummary();
  const update = useUpdatePfReport();
  const aiDraft = report.status === 'draft' ? report.aiDraft ?? null : null;
  const [text, setText] = useState(aiDraft?.summary ?? '');
  const [writing, setWriting] = useState(false);
  const [own, setOwn] = useState(report.aiSummary ?? '');
  const [error, setError] = useState<string | null>(null);
  // A new draft replaces the box's text.
  useEffect(() => {
    setText(aiDraft?.summary ?? '');
  }, [aiDraft?.runId, aiDraft?.summary]);

  const isDraft = report.status === 'draft';
  const busy = draftAi.isPending || approve.isPending || dismiss.isPending || update.isPending;

  const askAi = () => {
    setError(null);
    draftAi.mutate(report.id, {
      onError: (err) => setError(pfDigRefusal(err, 'The AI could not draft a summary. Try again, or write it yourself.')),
    });
  };
  const doApprove = () => {
    if (!aiDraft) return;
    setError(null);
    const trimmed = text.trim();
    // Send the text only when the person changed it; otherwise the API copies the draft as written.
    const changed = trimmed !== (aiDraft.summary ?? '').trim();
    approve.mutate(
      { id: report.id, runId: aiDraft.runId, summary: changed ? trimmed : undefined },
      {
        onSuccess: () => toast.success('Summary approved'),
        onError: (err) => setError(pfDigRefusal(err, 'The summary could not be approved.')),
      },
    );
  };
  const doDismiss = () => {
    if (!aiDraft) return;
    setError(null);
    dismiss.mutate(
      { id: report.id, runId: aiDraft.runId },
      { onError: (err) => setError(pfDigRefusal(err, 'The draft could not be discarded.')) },
    );
  };
  const saveOwn = () => {
    setError(null);
    const t = own.trim();
    update.mutate(
      { id: report.id, body: { ai_summary: t === '' ? null : t } },
      {
        onSuccess: () => {
          toast.success('Summary saved');
          setWriting(false);
        },
        onError: (err) => setError(pfDigRefusal(err, 'The summary could not be saved.')),
      },
    );
  };

  return (
    <div className="space-y-3 border-t border-fieldiq-border pt-3" data-testid="pf-report-summary">
      <div>
        <p className={LABEL}>Summary</p>
        {writing ? (
          <div className="mt-1 space-y-2" data-testid="pf-report-summary-write">
            <SummaryTextarea
              aria-label="Report summary"
              value={own}
              maxLength={3000}
              onChange={(e) => setOwn(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className={PF_SECONDARY} onClick={() => setWriting(false)} disabled={update.isPending}>
                Cancel
              </button>
              <button type="button" className={PF_PRIMARY} onClick={saveOwn} disabled={update.isPending} data-testid="pf-report-summary-save">
                {update.isPending ? 'Saving…' : 'Save summary'}
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-1 whitespace-pre-wrap text-sm text-fieldiq-text" data-testid="pf-report-summary-text">
            {report.aiSummary || '—'}
          </p>
        )}
      </div>

      <PfFormError message={error} />

      {/* aiDraft is already null on an issued report (above): that one test is the gate, so it can be falsified alone. */}
      {aiDraft && (
        <div
          data-testid="pf-report-ai-draft"
          className="space-y-2 rounded-fi-control border border-fi-amber/60 border-l-4 border-l-fi-amber bg-fi-signal-amber/10 px-4 py-3"
        >
          <p className="flex items-center gap-1.5 text-sm font-bold text-fieldiq-text">
            <Sparkles className="size-4" aria-hidden="true" />
            AI draft — awaiting your approval
          </p>
          <p className="text-xs text-fieldiq-muted">
            Written by the AI from this report&apos;s figures{aiDraft.createdAt ? ` on ${formatInstant(aiDraft.createdAt, timeZone) ?? '—'}` : ''}. It is not part of the
            report until approved. Edit it as needed.
          </p>
          {canWrite ? (
            <SummaryTextarea
              aria-label="AI draft summary"
              value={text}
              maxLength={3000}
              onChange={(e) => setText(e.target.value)}
            />
          ) : (
            <p className="whitespace-pre-wrap text-sm text-fieldiq-text">{aiDraft.summary || '—'}</p>
          )}
          {canWrite && (
            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" className={PF_SECONDARY} onClick={doDismiss} disabled={busy} data-testid="pf-report-ai-dismiss">
                {dismiss.isPending ? 'Discarding…' : 'Dismiss'}
              </button>
              <button type="button" className={PF_PRIMARY} onClick={doApprove} disabled={busy || !text.trim()} data-testid="pf-report-ai-approve">
                <CheckCircle2 className="size-4" aria-hidden="true" />
                {approve.isPending ? 'Approving…' : 'Approve summary'}
              </button>
            </div>
          )}
        </div>
      )}

      {isDraft && canWrite && !writing && (
        <div className="flex flex-wrap gap-2">
          {!aiDraft && (
            <button type="button" className={PF_SECONDARY} onClick={askAi} disabled={busy} data-testid="pf-report-ai-draft-button">
              <Sparkles className="size-4" aria-hidden="true" />
              {draftAi.isPending ? 'Drafting…' : 'Draft summary with AI'}
            </button>
          )}
          <button
            type="button"
            className={PF_SECONDARY}
            onClick={() => { setError(null); setOwn(report.aiSummary ?? ''); setWriting(true); }}
            disabled={busy}
            data-testid="pf-report-summary-edit"
          >
            {report.aiSummary ? 'Edit summary' : 'Write summary'}
          </button>
        </div>
      )}
      {draftAi.isPending && (
        <p className="text-sm text-fieldiq-muted" role="status" data-testid="pf-report-ai-drafting">
          Drafting the summary from this report&apos;s figures…
        </p>
      )}
    </div>
  );
}
